import { FC, Fragment, useState } from "react";
import { Send } from "@mui/icons-material";
import { useAccount } from "wagmi";

import { useMetamask, useWriteContractWithSimulation } from "@dzambalaorg/react-hooks-eth";
import { ListAction, ListActionVariant } from "@framework/styled";
import { AddressType, ContractFeatures, type IToken, TokenStatus } from "@framework/types";
import ERC721SafeTransferFromABI from "@framework/abis/json/ERC721Simple/safeTransferFrom.json";

import { AccountDialog, IAccountDto } from "../../../../dialogs/account";

interface IErc721TransferButtonProps {
  className?: string;
  disabled?: boolean;
  token: IToken;
  variant?: ListActionVariant;
}

export const Erc721TransferButton: FC<IErc721TransferButtonProps> = props => {
  const { className, disabled, token, variant = ListActionVariant.button } = props;

  const [isTransferTokenDialogOpen, setIsTransferTokenDialogOpen] = useState(false);

  const { writeContractAsync } = useWriteContractWithSimulation();
  const { address: walletAddress = "" } = useAccount();

  const { execute: metaFn } = useMetamask(async (dto: IAccountDto) => {
    return writeContractAsync({
      address: token.template!.contract!.address as AddressType,
      abi: ERC721SafeTransferFromABI,
      functionName: "safeTransferFrom",
      args: [walletAddress, dto.account, token.tokenId],
    });
  });

  const handleTransfer = (): void => {
    setIsTransferTokenDialogOpen(true);
  };

  const handleTransferConfirm = async (dto: IAccountDto) => {
    await metaFn(dto).finally(() => {
      setIsTransferTokenDialogOpen(false);
    });
  };

  const handleTransferCancel = () => {
    setIsTransferTokenDialogOpen(false);
  };

  if (token.tokenStatus === TokenStatus.BURNED) {
    return null;
  }

  return (
    <Fragment>
      <ListAction
        icon={Send}
        onClick={handleTransfer}
        message="form.buttons.transfer"
        className={className}
        dataTestId="Erc721TransferButton"
        disabled={disabled || token.template?.contract?.contractFeatures.includes(ContractFeatures.SOULBOUND)}
        variant={variant}
      />
      <AccountDialog
        onConfirm={handleTransferConfirm}
        onCancel={handleTransferCancel}
        open={isTransferTokenDialogOpen}
        message="dialogs.transfer"
        testId="Erc721TransferDialogForm"
        initialValues={{
          account: "",
        }}
      />
    </Fragment>
  );
};
